import {
  EnumerationsAAPReportCategory,
  EnumerationsAAPReportClassification,
  EnumerationsAAPReportSettingsCategory,
  EnumerationsAdverseImpactDisplayOption,
  EnumerationsComplianceType,
  EnumerationsDataVisualizationType,
  EnumerationsDocumentCenterSortColumn,
  EnumerationsFileType,
  EnumerationsNumericDisplayOption,
  EnumerationsOverviewSection,
  EnumerationsPersonnelActivityResultsDisplayed,
  EnumerationsPlanSummaryAIShowFor,
  EnumerationsPlanSummaryIncvsEstShowFor,
  EnumerationsPlanType,
  EnumerationsRecurringInterval,
  EnumerationsRole,
  EnumerationsRunByForProtectedClasses,
  EnumerationsSortDirection,
} from 'client'
import {
  ClientManageClientsSortColumn,
  UserManageUsersSortColumn,
} from 'client/identity'
import {
  displayReportToggleToReportSetting,
  getEnumValueFromReportSettings,
} from './reportSettings'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type {
  QueriesDocumentCenterUser,
  QueriesGetAllClientsRow,
  QueriesGetAllUsersRow,
  TransferObjectsPlanInfo,
  UserAvailableUser,
} from 'client/identity'
import type {
  CommandsUpdatePlanSummaryReportSettingsCommand,
  DataVizNullableCountPercentByEntity,
  GoalDemographicWithListOfLocationsDTO,
  GoalEEOCodeBradyBunchResponse,
  OverviewOverviewSectionSettingsDTO,
  OverviewSelectedActionTypesDTO,
  PlansLevelOfAnalysisDTO,
  QueriesGetGoalBradyBunchDrilldownLevel2Response,
  QueriesGetPlanSummaryReportSettingsResponse,
  TransferObjectsGetDocumentUploadDTO,
  TransferObjectsSubPlanResponseDTO,
} from 'client'
import type {
  IClientManagementTableRowData,
  IDocumentCenterTableRowData,
  IUserManagementTableRowData,
} from '~/constants/types/table'
import type { IServiceLineItem } from '~/constants/types/forms'
import type {
  IBradyBunchTile,
  IGoal,
  IGoalsByJobTitleForJobGroup,
  ILocation,
  IPrepareBarChartData,
  TDirection,
} from '~/constants/types/charts'
import type {
  ActionType,
  ActionTypeOption,
  IReportSettingsCategory,
} from '~/constants/types/dataViz'
import type { TFileTypeName, TFilesWithIcons } from '~/constants/types/files'
import type {
  IPlanSummarySettings,
  IReportSettingOption,
  ISelectedActionTypes,
} from '~/constants/types/reportSettings'
import type { ILeftRightOption } from '~/constants/types'
import {
  ODataVisualizationRouteNames,
  type TDataVisualizationRouteNames,
} from '~/constants/navigation'
import { OFileTypeNames } from '~/constants/types/files'
import { COPY } from '~/constants/strings'

// QTable Props to Request Params transforms
export const transformBooleanToSortDirection = (
  isAscendingOrder: boolean
): EnumerationsSortDirection => {
  return isAscendingOrder
    ? EnumerationsSortDirection.Ascending
    : EnumerationsSortDirection.Descending
}

export const transformDescendingBooleanToEnumValue = (
  isDescending: boolean | null
): EnumerationsSortDirection | null => {
  if (isDescending) return EnumerationsSortDirection.Descending
  if (isDescending === false) return EnumerationsSortDirection.Ascending
  return null
}

export const transformStringToManageClientsSortColumn = (
  str: string
): ClientManageClientsSortColumn | null => {
  switch (Number(str)) {
    case ClientManageClientsSortColumn.ClientName:
      return ClientManageClientsSortColumn.ClientName
    case ClientManageClientsSortColumn.ServiceLines:
      return ClientManageClientsSortColumn.ServiceLines
    case ClientManageClientsSortColumn.Status:
      return ClientManageClientsSortColumn.Status
    default:
      return null
  }
}

export const transformStringToManageUsersSortColumn = (
  str: string
): UserManageUsersSortColumn | null => {
  switch (Number(str)) {
    case UserManageUsersSortColumn.UserName:
      return UserManageUsersSortColumn.UserName
    case UserManageUsersSortColumn.Email:
      return UserManageUsersSortColumn.Email
    case UserManageUsersSortColumn.Role:
      return UserManageUsersSortColumn.Role
    case UserManageUsersSortColumn.ServiceLines:
      return UserManageUsersSortColumn.ServiceLines
    case UserManageUsersSortColumn.Status:
      return UserManageUsersSortColumn.Status
    default:
      return null
  }
}

/**
 * Transforms a string with the name of the enum DocumentCenterSortColumn or the number of the enum to a DocumentCenterSortColumn enum value or null.
 *
 * @param {string | number} input - The input string or number to be transformed.
 * @return {DocumentCenterSortColumn | null} The corresponding enum value or null if not found.
 */
export const transformStringToDocumentCenterSortColumn = (
  input: string | number
): EnumerationsDocumentCenterSortColumn | null => {
  // If input is a number and it's a valid enum value, return the enum
  if (typeof input === 'number') {
    if (Object.values(EnumerationsDocumentCenterSortColumn).includes(input)) {
      return input as EnumerationsDocumentCenterSortColumn
    }
  } else {
    // Handle string input
    // Check if the string is exactly an enum name
    if (input in EnumerationsDocumentCenterSortColumn) {
      return EnumerationsDocumentCenterSortColumn[
        input as keyof typeof EnumerationsDocumentCenterSortColumn
      ]
    }

    // If using Name of the Enum match to correct Enum
    switch (input) {
      case 'Sender':
        return EnumerationsDocumentCenterSortColumn.Sender
      case 'Recipient':
        return EnumerationsDocumentCenterSortColumn.Recipient
      case 'Company':
        return EnumerationsDocumentCenterSortColumn.Company
      case 'ClientAccount':
        return EnumerationsDocumentCenterSortColumn.ClientAccount
      case 'Subject':
        return EnumerationsDocumentCenterSortColumn.Subject
      case 'DateSubmitted':
        return EnumerationsDocumentCenterSortColumn.DateSubmitted
      default:
        return null
    }
  }

  return null
}

// * Friendly Transform aka Transform to Displayable String
export const transformAAPReportClassificationToFriendlyAAPReportClassification =
  (
    aapReportClassification: EnumerationsAAPReportClassification | null
  ): string => {
    switch (aapReportClassification) {
      case EnumerationsAAPReportClassification.Primary:
        return 'Primary'
      case EnumerationsAAPReportClassification.Support:
        return 'Support'
      default:
        return ''
    }
  }

export const transformAAPReportSettingsCategoryToFriendlyAAPReportSettingsCategory =
  (
    aapReportSettingsCategory: EnumerationsAAPReportSettingsCategory | null
  ): string => {
    switch (aapReportSettingsCategory) {
      case EnumerationsAAPReportSettingsCategory.AdverseImpact:
        return 'Adverse Impact'
      case EnumerationsAAPReportSettingsCategory.ApplicantSummary:
        return 'Applicant Summary'
      case EnumerationsAAPReportSettingsCategory.Availability:
        return 'Availability'
      case EnumerationsAAPReportSettingsCategory.DataCollection:
        return 'Data Collection'
      case EnumerationsAAPReportSettingsCategory.JobGroupAnalysis:
        return 'Job Group Analysis'
      case EnumerationsAAPReportSettingsCategory.PlanSummary:
        return 'Plan Summary'
      case EnumerationsAAPReportSettingsCategory.WorkforceAnalysis:
        return 'Workforce Analysis'
      default:
        return ''
    }
  }

export const transformAAPReportCategoryToFriendlyAAPReportCategory = (
  aapReportCategory: EnumerationsAAPReportCategory | null
): string => {
  switch (aapReportCategory) {
    case EnumerationsAAPReportCategory.AdverseImpact:
      return 'Adverse Impact'
    case EnumerationsAAPReportCategory.Availability:
      return 'Availability'
    case EnumerationsAAPReportCategory.Communication:
      return 'Communication'
    case EnumerationsAAPReportCategory.Compensation:
      return 'Compensation'
    case EnumerationsAAPReportCategory.Employment:
      return 'Employment'
    case EnumerationsAAPReportCategory.IWDVeteran:
      return 'IWD / Veteran'
    case EnumerationsAAPReportCategory.Monitoring:
      return 'Monitoring'
    case EnumerationsAAPReportCategory.Narratives:
      return 'Narratives'
    case EnumerationsAAPReportCategory.PersonnelActions:
      return 'Personnel Actions'
    case EnumerationsAAPReportCategory.Workforce:
      return 'Workforce'
    default:
      return ''
  }
}

export const transformRoleToFriendlyRole = (
  userRole: EnumerationsRole
): string => {
  switch (userRole) {
    case EnumerationsRole.Admin:
      return 'Admin'
    case EnumerationsRole.BerkshireDataAdmin:
      return 'Berkshire Data Admin'
    case EnumerationsRole.BerkshireCSLeadership:
      return 'Berkshire CS Leadership'
    case EnumerationsRole.BerkshirePSAdmin:
      return 'Berkshire Product Support Admin'
    case EnumerationsRole.BerkshireConsultant:
      return 'Berkshire Consultant'
    case EnumerationsRole.BerkshireProductSupport:
      return 'Berkshire Product Support'
    case EnumerationsRole.BerkshireSystemAdmin:
      return 'Berkshire System Admin'
    case EnumerationsRole.CompetitiveConsultant:
      return 'Competitive Consultant'
    case EnumerationsRole.ReportDataVizandInsightsViewer:
      return 'Report, Dataviz, and Insights Viewer'
    case EnumerationsRole.ReportViewer:
      return 'Report Viewer'
    case EnumerationsRole.ReportandDataVizViewer:
      return 'Report and Dataviz Viewer'
    case EnumerationsRole.ServiceOwner:
      return 'Service Owner'
    case EnumerationsRole.ServiceDeliverableViewer:
      return 'Service Deliverable Viewer'
    case EnumerationsRole.DataVizOnlyViewer:
      return 'Dataviz Only Viewer'
    case EnumerationsRole.NonReportsServiceOwner:
      return 'Non-Reports Service Owner'
    default:
      return ''
  }
}

export const transformOverviewSectionToFriendlyOverviewSection = (
  overviewSection: EnumerationsOverviewSection
): string => {
  switch (overviewSection) {
    case EnumerationsOverviewSection.AdverseImpact:
      return 'Adverse Impact'
    case EnumerationsOverviewSection.ApplicantFlow:
      return 'Applicant Flow'
    case EnumerationsOverviewSection.Employment:
      return 'Employment'
    case EnumerationsOverviewSection.GoalAttainment:
      return 'Goal Attainment'
    case EnumerationsOverviewSection.Goals:
      return 'Goals'
    case EnumerationsOverviewSection.PersonnelActivity:
      return 'Personnel Activity'
    case EnumerationsOverviewSection.ProtectedVeterans:
      return 'Protected Veterans'
    case EnumerationsOverviewSection.IWD:
      return 'IWD'

    // case EnumerationsOverviewSection.RecruitmentAreas:
    //   return 'Recruitment Areas'

    // case OverviewSettingsType.Custom:
    default:
      return ''
  }
}

export const getReadableIntervalName = (
  intervalId: EnumerationsRecurringInterval
) => {
  switch (intervalId) {
    case EnumerationsRecurringInterval.Never:
      return 'None'
    case EnumerationsRecurringInterval.Monthly:
      return 'Monthly'
    case EnumerationsRecurringInterval.Quarterly:
      return 'Quarterly'
    case EnumerationsRecurringInterval.Annually:
      return 'Annually'
    default:
      return ''
  }
}

export const transformRouteNameToDataVisualizationType = (
  path: RouteLocationNormalizedLoaded['name']
): EnumerationsDataVisualizationType | undefined => {
  switch (path) {
    case ODataVisualizationRouteNames.Employment:
      return EnumerationsDataVisualizationType.Employment
    case ODataVisualizationRouteNames.Goals:
      return EnumerationsDataVisualizationType.Goals
    case ODataVisualizationRouteNames.GoalAttainment:
      return EnumerationsDataVisualizationType.GoalAttainment
    case ODataVisualizationRouteNames.ApplicantFlow:
      return EnumerationsDataVisualizationType.ApplicantFlow
    case ODataVisualizationRouteNames.AdverseImpact:
      return EnumerationsDataVisualizationType.AdverseImpact
    case ODataVisualizationRouteNames.PersonnelActivity:
      return EnumerationsDataVisualizationType.PersonnelActivity
    case ODataVisualizationRouteNames.IndividualsWithDisabilities:
      return EnumerationsDataVisualizationType.IWD
    case ODataVisualizationRouteNames.Overview:
      return EnumerationsDataVisualizationType.Overview
    case ODataVisualizationRouteNames.ProtectedVeterans:
      return EnumerationsDataVisualizationType.ProtectedVeterans
    default:
      return undefined
  }
}

export const transformOverviewSectionToDataVisualizationRouteName = (
  overviewSection: EnumerationsOverviewSection
): TDataVisualizationRouteNames => {
  switch (overviewSection) {
    case EnumerationsOverviewSection.AdverseImpact:
      return ODataVisualizationRouteNames.AdverseImpact
    case EnumerationsOverviewSection.ApplicantFlow:
      return ODataVisualizationRouteNames.ApplicantFlow
    case EnumerationsOverviewSection.Employment:
      return ODataVisualizationRouteNames.Employment
    case EnumerationsOverviewSection.GoalAttainment:
      return ODataVisualizationRouteNames.GoalAttainment
    case EnumerationsOverviewSection.Goals:
      return ODataVisualizationRouteNames.Goals
    case EnumerationsOverviewSection.IWD:
      return ODataVisualizationRouteNames.IndividualsWithDisabilities
    case EnumerationsOverviewSection.PersonnelActivity:
      return ODataVisualizationRouteNames.PersonnelActivity
    case EnumerationsOverviewSection.ProtectedVeterans:
      return ODataVisualizationRouteNames.ProtectedVeterans
  }
}

export const transformStringToFileType = (
  str: string
): EnumerationsFileType => {
  switch (str) {
    case '.accdb':
    case '.mdb':
      return EnumerationsFileType.Access
    case '.csv':
      return EnumerationsFileType.CSV
    case '.gif':
    case 'image/gif':
      return EnumerationsFileType.GIF
    case '.jpeg':
    case '.jpg':
    case 'image/jpg':
    case 'image/jpeg':
      return EnumerationsFileType.JPEG
    case '.png':
    case 'image/png':
      return EnumerationsFileType.PNG
    case '.rtf':
      return EnumerationsFileType.RTF
    case '.svg':
    case 'image/svg':
    case 'image/svg+xml':
      return EnumerationsFileType.SVG
    case '.zip':
    case 'application/x-zip-compressed':
      return EnumerationsFileType.Zip
    case 'application/msword':
    case '.docx':
    case '.doc':
      return EnumerationsFileType.Word
    case 'application/pdf':
    case '.pdf':
      return EnumerationsFileType.PDF
    case 'application/vnd.ms-excel':
    case '.xlsx':
      return EnumerationsFileType.Excel
    case 'application/vnd.ms-powerpoint':
    case '.pptx':
      return EnumerationsFileType.PowerPoint
    case 'audio/mp4':
    case '.mp4':
      return EnumerationsFileType.MP4
    case 'text/plain':
    case '.txt':
      return EnumerationsFileType.Text
    default:
      return EnumerationsFileType.Invalid
  }
}

export const transformFileToFileType = (file: File): EnumerationsFileType => {
  // seeing that sometimes when you upload files there's dramatic names, either it finds the file.type or checks the extension after the period e.g. ".gif"
  const forcefullyAssignedExtension = `.${file.name.split('.')?.[1]}`
  return transformStringToFileType(file.type) === EnumerationsFileType.Invalid
    ? transformStringToFileType(forcefullyAssignedExtension)
    : transformStringToFileType(file.type)
}

export const transformFileTypeToTFileTypeName = (
  fileType: EnumerationsFileType
): TFileTypeName | '' => {
  switch (fileType) {
    case EnumerationsFileType.Access:
      return OFileTypeNames.ACCDB
    case EnumerationsFileType.CSV:
      return OFileTypeNames.CSV
    case EnumerationsFileType.Excel:
      return OFileTypeNames.EXCEL
    case EnumerationsFileType.GIF:
      return OFileTypeNames.GIF
    case EnumerationsFileType.JPEG:
      return OFileTypeNames.JPEG
    case EnumerationsFileType.MP4:
      return OFileTypeNames.MP4
    case EnumerationsFileType.PDF:
      return OFileTypeNames.PDF
    case EnumerationsFileType.PNG:
      return OFileTypeNames.PNG
    case EnumerationsFileType.PowerPoint:
      return OFileTypeNames.PPT
    case EnumerationsFileType.RTF:
      return OFileTypeNames.RTF
    case EnumerationsFileType.SVG:
      return OFileTypeNames.SVG
    case EnumerationsFileType.Text:
      return OFileTypeNames.TEXT
    case EnumerationsFileType.Word:
      return OFileTypeNames.DOC
    case EnumerationsFileType.Zip:
      return OFileTypeNames.ZIP
    default:
      return ''
  }
}

export const transformFileTypeToTFilesWithIcons = (
  fileType: EnumerationsFileType
): TFilesWithIcons => {
  switch (fileType) {
    case EnumerationsFileType.MP4:
      return 'mov'
    case EnumerationsFileType.PDF:
      return 'pdf'
    case EnumerationsFileType.CSV:
    case EnumerationsFileType.Excel:
      return 'excel'
    default:
      return 'generic'
  }
}

export const transformResultRowsToClientManagementTableRowData = (
  resultsRows: QueriesGetAllClientsRow[]
): IClientManagementTableRowData[] => {
  return resultsRows?.length
    ? resultsRows.map(
        (
          resultsRow: QueriesGetAllClientsRow
        ): IClientManagementTableRowData => {
          return {
            clientID: resultsRow?.clientID,
            name: resultsRow?.clientName,
            serviceLines: resultsRow?.serviceLines,
            status: resultsRow?.status,
          }
        }
      )
    : []
}

export const transformResultRowsToUserManagementTableRowData = (
  resultsRows: QueriesGetAllUsersRow[]
): IUserManagementTableRowData[] => {
  return resultsRows.map(
    (resultsRow: QueriesGetAllUsersRow): IUserManagementTableRowData => {
      return {
        id: resultsRow?.userID,
        email: resultsRow?.email,
        role: resultsRow?.role,
        name: `${resultsRow?.firstName} ${resultsRow?.lastName}`,
        serviceLines: resultsRow?.serviceLines,
        status: resultsRow?.status,
      }
    }
  )
}

export const transformResultRowsToDocumentCenterTableRowData = (
  resultsRows: TransferObjectsGetDocumentUploadDTO[]
): IDocumentCenterTableRowData[] => {
  return resultsRows.map((resultsRow): IDocumentCenterTableRowData => {
    return {
      id: resultsRow.documentUploadID,
      sender: {
        senderEmail: resultsRow?.senderEmail,
        senderName: resultsRow?.senderName,
      },
      recipients: resultsRow?.recipients,
      clientAccount: resultsRow?.clientAccount,
      company: resultsRow?.company,
      dateSubmitted: resultsRow?.dateSubmitted,
      subject: resultsRow?.subject,
      selected: false,
      read: resultsRow.read,
      projectName: resultsRow.projectName,
      locationName: resultsRow.locationName,
    }
  })
}

export const transformClientServicesPayload = ({
  serviceLines = [],
  isEditing = false,
}: {
  serviceLines: IServiceLineItem[]
  isEditing: boolean
}): (string | number)[] => {
  const idToUse: keyof IServiceLineItem = !isEditing
    ? 'serviceTypeID'
    : 'serviceID'
  return serviceLines.map((s) => s[idToUse])
}

export const asyncBlobToBase64 = (
  blob: Blob
): Promise<FileReader['result']> => {
  return new Promise((resolve, _) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.readAsDataURL(blob)
  })
}

export const asyncBase64ToBlob = async (
  base64: string,
  type = 'application/octet-stream'
): Promise<Blob> => {
  const res = await fetch(`data:${type};base64,${base64}`)
  const blob = await res.blob()
  return blob
}

// Converts an ArrayBuffer directly to base64, without any intermediate 'convert to string then
// use window.btoa' step. According to my tests, this appears to be a faster approach:
// http://jsperf.com/encoding-xhr-image-data/5

/*
MIT LICENSE
Copyright 2011 Jon Leighton
Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:
The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/

/*
Modified by LittleSaya at 2022/10/19, 2022/11/4
*/

// Base 64 Encoding with URL and Filename Safe Alphabet
// https://datatracker.ietf.org/doc/html/rfc4648#section-5
const encodings =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_'

export function encodeArrayBufferToUrlSafeBase64(
  arrayBuffer: ArrayBuffer
): string {
  let base64 = ''

  const bytes = new Uint8Array(arrayBuffer)
  const byteLength = bytes.byteLength
  const byteRemainder = byteLength % 3
  const mainLength = byteLength - byteRemainder

  let a, b, c, d
  let chunk

  // Main loop deals with bytes in chunks of 3
  for (let i = 0; i < mainLength; i = i + 3) {
    // Combine the three bytes into a single integer
    chunk = (bytes[i] << 16) | (bytes[i + 1] << 8) | bytes[i + 2]

    // Use bitmasks to extract 6-bit segments from the triplet
    a = (chunk & 16515072) >> 18 // 16515072 = (2^6 - 1) << 18
    b = (chunk & 258048) >> 12 // 258048   = (2^6 - 1) << 12
    c = (chunk & 4032) >> 6 // 4032     = (2^6 - 1) << 6
    d = chunk & 63 // 63       = 2^6 - 1

    // Convert the raw binary segments to the appropriate ASCII encoding
    base64 += encodings[a] + encodings[b] + encodings[c] + encodings[d]
  }

  // Deal with the remaining bytes and padding
  if (byteRemainder === 1) {
    chunk = bytes[mainLength]

    a = (chunk & 252) >> 2 // 252 = (2^6 - 1) << 2

    // Set the 4 least significant bits to zero
    b = (chunk & 3) << 4 // 3   = 2^2 - 1

    base64 += encodings[a] + encodings[b] // + '=='
  } else if (byteRemainder === 2) {
    chunk = (bytes[mainLength] << 8) | bytes[mainLength + 1]

    a = (chunk & 64512) >> 10 // 64512 = (2^6 - 1) << 10
    b = (chunk & 1008) >> 4 // 1008  = (2^6 - 1) << 4

    // Set the 2 least significant bits to zero
    c = (chunk & 15) << 2 // 15    = 2^4 - 1

    base64 += encodings[a] + encodings[b] + encodings[c] // + '='
  }

  return base64
}

function charCodeToNumber(charCode: number): number {
  if (charCode >= 65 && charCode <= 90) {
    return charCode - 65
  } else if (charCode >= 97 && charCode <= 122) {
    return charCode - 71
  } else if (charCode >= 48 && charCode <= 57) {
    return charCode + 4
  } else if (charCode === 45) {
    return 62
  } else if (charCode === 95) {
    return 63
  } else {
    throw new Error(`Invalid char code in url safe base64: ${charCode}`)
  }
}

export function decodeUrlSafeBase64ToArrayBuffer(str: string): ArrayBuffer {
  const mod4 = str.length % 4
  let mainByteLength = 0
  let mainStringLength = 0
  let restByteLength = 0
  if (mod4 === 0) {
    mainByteLength = (str.length / 4) * 3
    mainStringLength = str.length
  } else if (mod4 === 1) {
    throw new Error(`invalid url safe base64 encoded string: ${str}`)
  } else if (mod4 === 2) {
    mainByteLength = ((str.length - 2) / 4) * 3
    mainStringLength = str.length - 2
    restByteLength = 1
  } else {
    mainByteLength = ((str.length - 3) / 4) * 3
    mainStringLength = str.length - 3
    restByteLength = 2
  }

  // 3-byte chunk
  let chunk
  const resultBuffer = new Uint8Array(mainByteLength + restByteLength)

  // process main string
  let a, b, c, d
  for (let i = 0, j = 0; i < mainStringLength; i += 4, j += 3) {
    a = charCodeToNumber(str.charCodeAt(i))
    b = charCodeToNumber(str.charCodeAt(i + 1))
    c = charCodeToNumber(str.charCodeAt(i + 2))
    d = charCodeToNumber(str.charCodeAt(i + 3))
    chunk = (a << 18) + (b << 12) + (c << 6) + d
    resultBuffer.set(
      [(chunk & 0xff0000) >> 16, (chunk & 0x00ff00) >> 8, chunk & 0x0000ff],
      j
    )
  }

  // process rest string
  if (mod4 === 2) {
    // 1 byte remain
    a = charCodeToNumber(str.charCodeAt(mainStringLength))
    b = charCodeToNumber(str.charCodeAt(mainStringLength + 1))
    chunk = (a << 2) + (b >> 4)
    resultBuffer.set([chunk & 0xff], mainByteLength)
  } else if (mod4 === 3) {
    // 2 bytes remain
    a = charCodeToNumber(str.charCodeAt(mainStringLength))
    b = charCodeToNumber(str.charCodeAt(mainStringLength + 1))
    c = charCodeToNumber(str.charCodeAt(mainStringLength + 2))
    chunk = (a << 10) + (b << 4) + (c >> 2)
    resultBuffer.set([(chunk & 0xff00) >> 8, chunk & 0x00ff], mainByteLength)
  }

  return resultBuffer
}

export const transformHTMLCanvasElementToBlob = (
  canvas: HTMLCanvasElement | undefined
): Promise<BlobPart> | void => {
  if (!canvas) return
  return new Promise<BlobPart>((resolve, _reject) => {
    canvas.toBlob((blob) => {
      resolve(blob as BlobPart)
    })
  })
}

export const octetStreamBlobToPDFBlob = (blob: Blob) => {
  return new Blob([blob], {
    type: 'application/pdf',
  })
}

export const transformBooleanToDirection = (
  direction: boolean | undefined
): TDirection => {
  return direction ? 'up' : 'down'
}

export const transformPlanResponseDTOToLevelOfAnalysis = (
  plan: TransferObjectsPlanInfo
): PlansLevelOfAnalysisDTO => {
  return {
    description: `${plan.establishment} (${plan.planCode})`,
    id: plan.planID,
    updatePlan: plan.updatePlan,
    planType: plan.planType,
    subPlans: null,
  }
}

export const transformActionTypeToActionTypeOption = (
  actionType: ActionType
): ActionTypeOption => {
  return {
    label: actionType.name,
    value: actionType.actionTypeId,
    positive: actionType.positive,
  }
}

export const transformAvailableUserToDocumentCenterUser = (
  user: UserAvailableUser
): QueriesDocumentCenterUser => {
  const nameParts = user.name.split(' ')
  const firstName = nameParts[0]
  const lastName = nameParts[1]
  return {
    email: user.email,
    userID: user.userID,
    firstName,
    lastName,
  }
}

export const transformDemographicWithListOfLocationsDTOToIGoal = (
  demographicWithListOfLocations: GoalDemographicWithListOfLocationsDTO
): IGoal => {
  const location: ILocation[] = demographicWithListOfLocations.locations.map(
    (location) => {
      const splitLocation = location.location.split(', ')
      const city = splitLocation[0]
      const state = splitLocation[1]
      return {
        city,
        percentage: location.percent,
        state,
      }
    }
  )
  return {
    demographic: demographicWithListOfLocations.demographic,
    ...(location.length > 0 && { location }),
    ...(location.length === 0 && { percentage: undefined }),
  }
}

export const transformGetGoalBradyBunchDrilldownLevel2ResponseToIGoalsByJobTitleForJobGroup =
  (
    response: QueriesGetGoalBradyBunchDrilldownLevel2Response | undefined
  ): IGoalsByJobTitleForJobGroup | undefined => {
    if (!response) {
      return
    }
    const goals: IGoal[] = response.demographicCountsByJobGroups?.map(
      (demographicCount) => {
        return transformDemographicWithListOfLocationsDTOToIGoal(
          demographicCount
        )
      }
    )
    return {
      goals,
      jobTitles: response.jobTitles,
      lowestFemaleJobTitle: response.lowestFemalePercent,
      lowestMinorityJobTitle: response.lowestMinorityPercent,
      jobTitleCount: response.totalCount,
    }
  }

export const transformEEOCodeBradyBunchResponseToIBradyBunchTile = (
  eeoCode: GoalEEOCodeBradyBunchResponse
): IBradyBunchTile => {
  return {
    eeoCode: eeoCode.eeoCode,
    eeoCodeGoals: `${eeoCode.count}`,
    eeoCodeJobGroups: `${eeoCode.numberOfJobGroups}`,
    eeoCodeName: eeoCode.eeoCodeDescription,
  }
}

export const transformIReportSettingOptionValueToAdverseImpactDisplayOption = (
  value: IReportSettingOption['value']
): EnumerationsAdverseImpactDisplayOption | null => {
  switch (value) {
    case 'byRaceGender':
      return EnumerationsAdverseImpactDisplayOption.ByRaceGender
    case 'byFavoredGroup':
      return EnumerationsAdverseImpactDisplayOption.ByFavoredGroup
    default:
      return null
  }
}

export const transformIReportSettingOptionValueToNumericDisplayOption = (
  value: IReportSettingOption['value']
): EnumerationsNumericDisplayOption | null => {
  switch (value) {
    case 'showAsNumbers':
      return EnumerationsNumericDisplayOption.ShowAsNumbers
    case 'showAsPercentages':
      return EnumerationsNumericDisplayOption.ShowAsPercentages
    default:
      return null
  }
}

export const transformIReportSettingOptionValueToPersonnelActivityResultsDisplayed =
  (
    value: IReportSettingOption['value']
  ): EnumerationsPersonnelActivityResultsDisplayed | null => {
    switch (value) {
      case 'breakoutIndividualActions':
        return EnumerationsPersonnelActivityResultsDisplayed.BreakoutIndividualActions
      case 'summarizePositiveAndNegative':
        return EnumerationsPersonnelActivityResultsDisplayed.SummarizePositiveAndNegative
      default:
        return null
    }
  }

export const transformNumericDisplayOptionToIReportSettingOptionArray = (
  numericDisplayOption?: EnumerationsNumericDisplayOption
): IReportSettingOption[] => {
  return [
    {
      label: COPY.SHOW_AS_NUMBERS,
      value: 'showAsNumbers',
      selected:
        numericDisplayOption === EnumerationsNumericDisplayOption.ShowAsNumbers,
    },
    {
      label: COPY.SHOW_AS_PERCENTAGES,
      value: 'showAsPercentages',
      selected:
        numericDisplayOption ===
        EnumerationsNumericDisplayOption.ShowAsPercentages,
    },
  ]
}

export const transformAdverseImpactDisplayOptionToIReportSettingOptionArray = (
  adverseImpactDisplayOption?: EnumerationsAdverseImpactDisplayOption
): IReportSettingOption[] => {
  return [
    {
      label: COPY.BY_RACE_GENDER,
      value: 'byRaceGender',
      selected:
        adverseImpactDisplayOption ===
        EnumerationsAdverseImpactDisplayOption.ByRaceGender,
    },
    {
      label: COPY.BY_FAVORED_GROUP,
      value: 'byFavoredGroup',
      selected:
        adverseImpactDisplayOption ===
        EnumerationsAdverseImpactDisplayOption.ByFavoredGroup,
    },
  ]
}

export const transformPersonnelActivityResultsDisplayedToIReportSettingOptionArray =
  (
    personnelActivityResultsDisplayed?: EnumerationsPersonnelActivityResultsDisplayed
  ): IReportSettingOption[] => {
    return [
      {
        label: COPY.BREAKOUT_INDIVIDUAL_ACTIONS,
        value: 'breakoutIndividualActions',
        selected:
          personnelActivityResultsDisplayed ===
          EnumerationsPersonnelActivityResultsDisplayed.BreakoutIndividualActions,
      },
      {
        label: COPY.SUMMARIZE_POSITIVE_AND_NEGATIVE,
        value: 'summarizePositiveAndNegative',
        selected:
          personnelActivityResultsDisplayed ===
          EnumerationsPersonnelActivityResultsDisplayed.SummarizePositiveAndNegative,
      },
    ]
  }

export const transformSelectedActionTypesDTOToIReportSettingOption = (
  selectedActionTypesDTO: OverviewSelectedActionTypesDTO
): IReportSettingOption => {
  return {
    label: selectedActionTypesDTO.actionTypeName ?? '',
    value: selectedActionTypesDTO.actionTypeName ?? '',
    selected: selectedActionTypesDTO.isSelected ?? false,
  }
}

export const transformIReportSettingsCategoryToOverviewSectionSettingsDTO = (
  settingsCategory: IReportSettingsCategory
): OverviewOverviewSectionSettingsDTO => {
  return {
    overviewSectionID: settingsCategory.overviewSectionID,
    isSelected: settingsCategory.isSelected,
  }
}

export const transformOverviewSectionSettingsDTOToIReportSettingsCategory = (
  sectionSettings: OverviewOverviewSectionSettingsDTO
): IReportSettingsCategory => {
  const settingsCategoryConfig: Omit<IReportSettingsCategory, 'isSelected'>[] =
    [
      {
        overviewSectionID: EnumerationsOverviewSection.Employment,
        isExpanded: true,
      },
      {
        overviewSectionID: EnumerationsOverviewSection.Goals,
        isExpanded: true,
      },
      {
        overviewSectionID: EnumerationsOverviewSection.PersonnelActivity,
        isExpanded: true,
      },
      {
        overviewSectionID: EnumerationsOverviewSection.AdverseImpact,
        isExpanded: true,
      },
      {
        overviewSectionID: EnumerationsOverviewSection.ApplicantFlow,
        isExpanded: true,
      },
      {
        overviewSectionID: EnumerationsOverviewSection.GoalAttainment,
        isExpanded: true,
      },
      {
        overviewSectionID: EnumerationsOverviewSection.ProtectedVeterans,
        isExpanded: undefined,
      },
      {
        overviewSectionID: EnumerationsOverviewSection.IWD,
        isExpanded: undefined,
      },
    ]

  const sectionSettingDefaults = settingsCategoryConfig.find(
    (settingsCategory) => {
      return (
        settingsCategory.overviewSectionID === sectionSettings.overviewSectionID
      )
    }
  )

  return {
    ...sectionSettings,
    isExpanded: sectionSettingDefaults?.isExpanded,
  }
}

export const transformNullableCountPercentByEntityToIPrepareBarChartData = (
  nullableCountPercentByEntityArray: DataVizNullableCountPercentByEntity[]
): IPrepareBarChartData[] => {
  return nullableCountPercentByEntityArray.map(
    (nullableCountPercentByEntity) => {
      return {
        label: nullableCountPercentByEntity.description,
        demographicCounts: nullableCountPercentByEntity.countPercents.map(
          (countPercents) => {
            return {
              demographic: countPercents.description,
              count: countPercents.count ?? 0,
              resultsByToDisplayFor: null,
            }
          }
        ),
      }
    }
  )
}

export const transformISelectedActionTypesToSelectedActionTypesDTO = (
  iSelectedActionTypesArray: ISelectedActionTypes[]
): OverviewSelectedActionTypesDTO[] => {
  return iSelectedActionTypesArray.map((paOption) => {
    return {
      positive: paOption.positive,
      actionTypeName: paOption.label,
      isSelected: paOption.selected,
    }
  })
}

export const transformSelectedActionTypesDTOToISelectedActionTypes = (
  selectedActionTypesDTO: OverviewSelectedActionTypesDTO[]
) => {
  return selectedActionTypesDTO.map((actionType) => {
    return {
      ...actionType,
      selected: actionType.isSelected,
      label: actionType.actionTypeName,
      value: actionType.actionTypeName,
    }
  })
}

export const transformGetPlanSummaryReportSettingsResponseToIPlanSummarySettings =
  (
    planSummarySettings: QueriesGetPlanSummaryReportSettingsResponse
  ): IPlanSummarySettings => {
    // group the options together that should be grouped and construct data structures that can be used for q-option-group
    // need to have arrays of labeled items with a single chosen item
    // then on submit, martial back to BE format, sending back true for radio groups if the option === the v-model for that group

    // radio group
    const whatProtectedClassesOptions = [
      {
        label: 'Run for Min/Fem',
        value: 'RunForMinFem',
        selected:
          planSummarySettings?.runByForProtectedClasses ===
          EnumerationsRunByForProtectedClasses.RunForMinFem,
      },
      {
        label: 'Run for protected classes selected in plan settings',
        value: 'RunForProtectedClassesInPlan',
        selected:
          planSummarySettings?.runByForProtectedClasses ===
          EnumerationsRunByForProtectedClasses.RunForProtectedClassesInPlan,
      },
    ]

    // checkbox array
    const whatPersonnelActivitiesOptions: ISelectedActionTypes[] =
      transformSelectedActionTypesDTOToISelectedActionTypes(
        planSummarySettings?.actionTypes
      )

    // option group (two options to toggle a single value)
    const includeIndividualsWithDisabilitiesOptions = [
      {
        label:
          'Yes, I want to include analysis for Individuals with Disabilities.',
        value: 'includeIWDAnalysis',
        selected: planSummarySettings?.includeIWDAnalysis === true,
      },
      {
        label:
          "No, I don't want to include analysis for Individuals with Disabilities.",
        value: 'excludeIWDAnalysis',
        selected: planSummarySettings?.includeIWDAnalysis === false,
      },
    ]

    // radio group (two options to toggle a single value)
    const includeProtectedVeteranAnalysisOptions = [
      {
        label: 'Yes, I want to include analysis for Protected Veterans.',
        value: 'includeProtectedVetAnalysis',
        selected: planSummarySettings?.includeProtectedVetAnalysis === true,
      },
      {
        label: "No, I don't want to include analysis for Protected Veterans.",
        value: 'excludeProtectedVetAnalysis',
        selected: planSummarySettings?.includeProtectedVetAnalysis === false,
      },
    ]

    // radio group
    const groupsWithGoalsDisplayOptions = [
      {
        label: 'Show all groups',
        value: 'IncvsEstShowAllGroups',
        selected:
          planSummarySettings?.incvsEstShowFor ===
          EnumerationsPlanSummaryIncvsEstShowFor.IncvsEstShowAllGroups,
      },
      {
        label: 'Show groups w/ goals',
        value: 'IncvsEstShowGroupsWithGoals',
        selected:
          planSummarySettings?.incvsEstShowFor ===
          EnumerationsPlanSummaryIncvsEstShowFor.IncvsEstShowGroupsWithGoals,
      },
      {
        label: 'Show groups w/ significant goals',
        value: 'IncvsEstShowGroupsWithSignificantGoals',
        selected:
          planSummarySettings?.incvsEstShowFor ===
          EnumerationsPlanSummaryIncvsEstShowFor.IncvsEstShowGroupsWithSignificantGoals,
      },
    ]

    // radio group
    const adverseImpactTotalMinoritiesAndNonMinoritiesOptions = [
      {
        label: 'Include in Export',
        value: 'true',
        selected:
          planSummarySettings?.adverseImpactIncludeTotalMinoritiesAndNonMinorities ===
          true,
      },
      {
        label: 'Exclude in Export',
        value: 'false',
        selected:
          planSummarySettings?.adverseImpactIncludeTotalMinoritiesAndNonMinorities ===
          false,
      },
    ]

    // radio group
    const adverseImpactDisplayOptions = [
      {
        label: 'Show all groups',
        value: 'AIShowAllGroups',
        selected:
          planSummarySettings?.aiShowFor ===
          EnumerationsPlanSummaryAIShowFor.AIShowAllGroups,
      },
      {
        label: 'Show groups w/ Adverse IRA',
        value: 'AIShowGroupsWithAdverseIRA',
        selected:
          planSummarySettings?.aiShowFor ===
          EnumerationsPlanSummaryAIShowFor.AIShowGroupsWithAdverseIRA,
      },
      {
        label: 'Show groups w/ significant Adverse IRA',
        value: 'AIShowGroupsWithSignificantAdverseIRA',
        selected:
          planSummarySettings?.aiShowFor ===
          EnumerationsPlanSummaryAIShowFor.AIShowGroupsWithSignificantAdverseIRA,
      },
      {
        label: 'Do not show Adverse Impact',
        value: 'AIDoNotShowAI',
        selected:
          planSummarySettings?.aiShowFor ===
          EnumerationsPlanSummaryAIShowFor.AIDoNotShowAI,
      },
    ]

    const planSummarySettingsForDisplay: IPlanSummarySettings = {
      ...planSummarySettings,
      whatProtectedClassesOptions,
      whatPersonnelActivitiesOptions,
      groupsWithGoalsDisplayOptions,
      adverseImpactDisplayOptions,
      adverseImpactTotalMinoritiesAndNonMinoritiesOptions,
      includeIndividualsWithDisabilitiesOptions,
      includeProtectedVeteranAnalysisOptions,
      hideRecruitmentAreasSection:
        !!planSummarySettings?.hideRecruitmentAreasSection,
    }

    return planSummarySettingsForDisplay
  }

export const transformIPlanSummarySettingsToUpdatePlanSummaryReportSettingsCommand =
  (
    planSummarySettings: IPlanSummarySettings
  ): CommandsUpdatePlanSummaryReportSettingsCommand | undefined => {
    const aiShowFor: EnumerationsPlanSummaryAIShowFor | null =
      getEnumValueFromReportSettings(
        planSummarySettings.adverseImpactDisplayOptions,
        EnumerationsPlanSummaryAIShowFor
      ) ?? null

    const adverseImpactIncludeTotalMinoritiesAndNonMinorities = stringToBoolean(
      planSummarySettings.adverseImpactTotalMinoritiesAndNonMinoritiesOptions.find(
        (item) => {
          return item.selected
        }
      )?.value
    )

    const runByForProtectedClasses: EnumerationsRunByForProtectedClasses | null =
      getEnumValueFromReportSettings(
        planSummarySettings.whatProtectedClassesOptions,
        EnumerationsRunByForProtectedClasses
      ) ?? null

    const incvsEstShowFor: EnumerationsPlanSummaryIncvsEstShowFor | null =
      getEnumValueFromReportSettings(
        planSummarySettings.groupsWithGoalsDisplayOptions,
        EnumerationsPlanSummaryIncvsEstShowFor
      ) ?? null

    const includeProtectedVeteransOptions = displayReportToggleToReportSetting(
      planSummarySettings.includeProtectedVeteranAnalysisOptions
    ).includeProtectedVetAnalysis

    const includeIWDAnalysis = displayReportToggleToReportSetting(
      planSummarySettings.includeIndividualsWithDisabilitiesOptions
    ).includeIWDAnalysis

    const whatPersonnelActivitiesOptions: OverviewSelectedActionTypesDTO[] =
      transformISelectedActionTypesToSelectedActionTypesDTO(
        planSummarySettings.whatPersonnelActivitiesOptions
      )

    if (
      runByForProtectedClasses === null ||
      aiShowFor === null ||
      incvsEstShowFor === null
    ) {
      console.warn('invalid plan summary settings')
      return
    }

    const planSummarySettingsPayload: CommandsUpdatePlanSummaryReportSettingsCommand =
      {
        runByForProtectedClasses,
        aiShowFor,
        incvsEstShowFor,
        includeIWDAnalysis,
        includeProtectedVetAnalysis: includeProtectedVeteransOptions,
        hideRecruitmentAreasSection: true,
        actionTypes: whatPersonnelActivitiesOptions,
        adverseImpactIncludeTotalMinoritiesAndNonMinorities,
      }
    return planSummarySettingsPayload
  }

// converts 'true' to true and everything else to false
export const stringToBoolean = (str: string | undefined): boolean => {
  return str?.toLowerCase() === 'true'
}

export const transformSubPlanResponseToILeftRightOption = (
  arr: TransferObjectsSubPlanResponseDTO[]
): ILeftRightOption[] => {
  const moreThanOneUniqueCompany = (
    subPlanResponseDTO: TransferObjectsSubPlanResponseDTO[]
  ): boolean => {
    const definedCompany = subPlanResponseDTO.find(
      (item) => item.company
    )?.company

    if (!definedCompany) {
      return false
    }

    if (
      subPlanResponseDTO.every((item) => {
        return item.company === definedCompany
      })
    ) {
      return false
    }
    return true
  }

  const appendCompany: boolean = moreThanOneUniqueCompany(arr)
  return arr.map<ILeftRightOption>((p) => {
    return {
      label: `${p.establishment} ${appendCompany ? `(${p.company})` : ''}`,
      value: p.planID,
      checked: p.selected,
    }
  })
}

export const transformEnumerationComplianceTypeToString = (
  ct: EnumerationsComplianceType
): { short: string; long: string } => {
  switch (ct) {
    case EnumerationsComplianceType.EO11246:
      return { short: '11246', long: 'EO 11246' }
    case EnumerationsComplianceType.NonDiscrimination:
      return { short: 'NDP', long: 'Non-Discrimination' }
    case EnumerationsComplianceType.IWD503VETSVEVRAALegacy:
      return {
        short: '503/VETS (Legacy)',
        long: 'IWD 503 / VETS VEVRAA (Legacy)',
      }
    case EnumerationsComplianceType.IWD503VETSVEVRAA2026:
      return {
        short: '503/VETS',
        long: 'IWD 503 / VETS VEVRAA',
      }
    case EnumerationsComplianceType.StateandLocalReporting:
      return { short: 'SLR', long: 'State and Local Reporting' }
    case EnumerationsComplianceType.AttorneyClientPrivilege:
      return { short: 'ACP', long: 'Attorney Client Privilege' }
    default:
      return { short: '', long: '' }
  }
}

export const transformEnumerationPlanTypeToString = (
  planType: EnumerationsPlanType
): string => {
  switch (planType) {
    case EnumerationsPlanType.Regular:
      return 'Regular'
    case EnumerationsPlanType.Master:
      return 'Master'
    case EnumerationsPlanType.Sub:
      return 'Sub'
    default:
      return ''
  }
}
